import { BasicUser } from "types/topicflow";

import Avatar from "@components/avatar/avatar";
import { pluralize } from "@helpers/string";

type Collaborator = {
  collaborationCount: number;
  externalEventCount: number;
  relationship: string;
  user: BasicUser;
};

const totalInteractions = ({
  collaborationCount,
  externalEventCount,
}: Collaborator) => collaborationCount + externalEventCount;

// The breakdown behind the total, e.g. "6 meetings · 2 shared activities". Spelling it out on
// every row made the bars ragged, so it moves to the total's tooltip. Someone can reach the list
// on shared pull requests or issues alone, so a zero meeting count is dropped.
// Deliberately not "items" — that already means recognitions/feedback/action items in the profile.
const contributionLabel = ({
  collaborationCount,
  externalEventCount,
}: Collaborator) => {
  const parts = [];
  if (collaborationCount > 0) {
    parts.push(
      `${collaborationCount} ${pluralize("meeting", collaborationCount)}`,
    );
  }
  if (externalEventCount > 0) {
    parts.push(
      `${externalEventCount} shared ${pluralize("activity", externalEventCount)}`,
    );
  }
  return parts.join(" · ");
};

const relationshipLabels: Record<string, string> = {
  manager: "Manager",
  direct_report: "Direct report",
  peer: "Peer",
  teammate: "Teammate",
  collaborator: "Collaborator",
};

const LegendSwatch = ({
  className,
  children,
}: {
  className: string;
  children: string;
}) => (
  <span className="flex items-center gap-1.5">
    <span className={`size-2 rounded-sm ${className}`} />
    {children}
  </span>
);

/**
 * A stacked bar per collaborator: how much of the relationship is meetings and how much is shared
 * work. The bar length is the two printed numbers added together, so it can never disagree with
 * them — and the split answers "do we actually build things together, or only meet?".
 */
const WorkHistoryCollaboratorGraph = ({
  collaborators,
  avatarSize,
}: {
  collaborators: Collaborator[];
  avatarSize: number;
}) => {
  const busiest = Math.max(1, ...collaborators.map(totalInteractions));
  const anySharedActivity = collaborators.some(
    (collaborator) => collaborator.externalEventCount > 0,
  );

  return (
    <div className="flex flex-col gap-4" aria-label="Top collaborators">
      {anySharedActivity && (
        <div className="flex items-center gap-4 text-2xs text-gray-400">
          <LegendSwatch className="bg-tfcolor">Meetings</LegendSwatch>
          <LegendSwatch className="bg-sky-500">Shared activities</LegendSwatch>
        </div>
      )}

      {collaborators.map((collaborator) => (
        <div
          className="flex items-center gap-3 min-w-0"
          key={collaborator.user.id}
          // One tooltip for the whole row, so hovering the bar or the number both explain it.
          title={contributionLabel(collaborator)}
        >
          <Avatar size={avatarSize} user={collaborator.user} />
          <div className="min-w-0 flex-1">
            <div className="font-medium truncate">{collaborator.user.name}</div>
            <div className="text-xs text-gray-500">
              {relationshipLabels[collaborator.relationship] ||
                relationshipLabels.collaborator}
            </div>
          </div>
          <div className="h-1.5 w-32 shrink-0 overflow-hidden rounded-full bg-gray-100">
            {/* A 2px gap separates the segments rather than a border drawn around them. */}
            <div className="flex h-full gap-0.5">
              <div
                className="h-full rounded-full bg-tfcolor"
                style={{
                  width: `${(collaborator.collaborationCount / busiest) * 100}%`,
                }}
              />
              <div
                className="h-full rounded-full bg-sky-500"
                style={{
                  width: `${(collaborator.externalEventCount / busiest) * 100}%`,
                }}
              />
            </div>
          </div>
          {/* Fixed-width so every bar starts at the same x; the breakdown is on hover. */}
          <div className="w-7 shrink-0 text-right text-xs text-gray-500 tabular-nums">
            {totalInteractions(collaborator)}
          </div>
        </div>
      ))}
    </div>
  );
};

export default WorkHistoryCollaboratorGraph;
